import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Formik } from "formik";
import * as Yup from "yup";

import { addStaff, getRoles } from "src/api/settings";
import { SelectField, TextField } from "src/components/form";
import { Button } from "src/components/ui/button";
import { usePortalContext } from "src/context/PortalContext";
import { useDashboardContext } from "src/context/DashboardContext";
import { showSuccessToast } from "src/lib/utils";

interface StaffFormValues {
	first_name: string;
	last_name: string;
	email: string;
	phone: string;
	role_id: string;
	store_or_warehouse_id: string;
}

const initialValues: StaffFormValues = {
	first_name: "",
	last_name: "",
	email: "",
	phone: "",
	role_id: "",
	store_or_warehouse_id: "",
};

const validationSchema = Yup.object().shape({
	first_name: Yup.string().required("This field is required"),
	last_name: Yup.string().required("This field is required"),
	email: Yup.string().email("Enter a valid email").required("This field is required"),
	phone: Yup.string().required("This field is required"),
	role_id: Yup.string().required("This field is required"),
	store_or_warehouse_id: Yup.string().required("This field is required"),
});

const AddStaff = () => {
	const queryClient = useQueryClient();
	const closePortal = usePortalContext();
	const { entities } = useDashboardContext();

	const { mutate, isLoading } = useMutation(addStaff, {
		onSuccess: ({ message }) => {
			showSuccessToast(message ?? "");
			queryClient.invalidateQueries(["teams"]);
			closePortal();
		},
	});

	const { data: rawRoles } = useQuery({ queryKey: ["roles"], queryFn: () => getRoles(), suspense: true });

	const roleOptions = (rawRoles?.data ?? []).map(({ id, name }) => ({ label: name ?? "Unnamed", value: String(id) }));
	const entityOptions = entities.map(({ id, name }) => ({ label: name, value: String(id) }));

	return (
		<Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={(values) => mutate(values)}>
			{({ values, handleChange, handleSubmit, setFieldValue, errors, touched }) => (
				<form onSubmit={handleSubmit} className="tw-grid tw-gap-4">
					<div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
						<TextField
							label="First name"
							name="first_name"
							placeholder="First name"
							value={values.first_name}
							error={touched.first_name && errors.first_name}
							onChange={handleChange}
							required
						/>
						<TextField
							label="Last name"
							name="last_name"
							placeholder="Last name"
							value={values.last_name}
							error={touched.last_name && errors.last_name}
							onChange={handleChange}
							required
						/>
						<TextField
							label="Email"
							name="email"
							type="email"
							placeholder="name@example.com"
							value={values.email}
							error={touched.email && errors.email}
							onChange={handleChange}
							required
						/>
						<TextField
							label="Phone number"
							name="phone"
							placeholder="Phone number"
							value={values.phone}
							error={touched.phone && errors.phone}
							onChange={handleChange}
							required
						/>
					</div>

					<SelectField
						label="Role"
						name="role_id"
						placeholder="Select a role"
						value={values.role_id}
						error={touched.role_id && errors.role_id}
						onChange={(value) => setFieldValue("role_id", value)}
						options={roleOptions}
						emptyMessage="No roles yet"
						required
					/>

					<SelectField
						label="Store / warehouse"
						name="store_or_warehouse_id"
						placeholder="Select store or warehouse"
						value={values.store_or_warehouse_id}
						error={touched.store_or_warehouse_id && errors.store_or_warehouse_id}
						onChange={(value) => setFieldValue("store_or_warehouse_id", value)}
						options={entityOptions}
						emptyMessage="No stores or warehouses yet"
						required
					/>

					<Button type="submit" variant="secondary" className="tw-mt-1 tw-w-full" disabled={isLoading}>
						{isLoading ? "Adding..." : "Add team member"}
					</Button>
				</form>
			)}
		</Formik>
	);
};

export default AddStaff;
